import { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PortalEventSwitcher from './portalEventSwitcher';
import { PortalActionsSlotContext } from './portalCommon';
import { secretaryPortalBase, tvLogsScrollingUrl, tvLogsUrl } from './portalPaths';
import { getClubs, portalErrorMessage } from '../../features/portal/portalService';

const appBarActionsSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  gap: 1,
  minWidth: 0,
  '& .MuiButton-outlined': {
    color: 'inherit',
    borderColor: 'rgba(255, 255, 255, 0.55)'
  },
  '& .MuiButton-contained': {
    backgroundColor: 'common.white',
    color: 'primary.main',
    '&:hover': {
      backgroundColor: 'grey.100'
    }
  },
  '& .MuiIconButton-root': {
    color: 'inherit'
  }
};

// Page actions portalled into the drawer lose their app-bar button styling and
// become the same full-width rows as the menu items below them.
const drawerActionsSx = {
  display: 'flex',
  flexDirection: 'column',
  borderBottom: 1,
  borderColor: 'divider',
  '&:empty': {
    display: 'none'
  },
  '& .MuiButton-root': {
    justifyContent: 'flex-start',
    width: '100%',
    minHeight: 48,
    px: 2,
    py: 1,
    borderRadius: 0,
    border: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'text.primary',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    textAlign: 'left',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'action.hover',
      boxShadow: 'none'
    },
    '&.Mui-disabled': {
      color: 'text.disabled'
    }
  },
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: 2,
    color: 'text.secondary',
    '& > *:nth-of-type(1)': {
      fontSize: 24
    }
  },
  '& .MuiButton-endIcon': {
    marginLeft: 'auto'
  },
  '& .MuiStack-root': {
    width: '100%',
    gap: '0 !important'
  },
  '& .MuiStack-root > :not(style) + :not(style)': {
    marginLeft: '0 !important',
    marginTop: '0 !important'
  }
};

const drawerItemSx = { minHeight: 48, px: 2 };
const drawerIconSx = { minWidth: 40, color: 'text.secondary' };

const PortalLayout = ({ session, token, onLogout, children }) => {
  const theme = useTheme();
  const location = useLocation();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));
  const [desktopActions, setDesktopActions] = useState(null);
  const [mobileActions, setMobileActions] = useState(null);
  const [headerActions, setHeaderActions] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tvDialogOpen, setTvDialogOpen] = useState(false);
  const [tvVenue, setTvVenue] = useState('');
  const [tvVenues, setTvVenues] = useState([]);
  const [tvVenuesLoading, setTvVenuesLoading] = useState(false);
  const [tvVenuesError, setTvVenuesError] = useState('');
  const [tvVenuesReload, setTvVenuesReload] = useState(0);
  const eventName = session?.event?.name || 'BowlScout event';
  const isSecretary = session?.scope?.kind === 'secretary';
  const identityName = String(session?.identity?.name || '').trim();
  const scopeLabel = isSecretary
    ? 'Tournament Secretary'
    : `Club: ${session?.scope?.venueName || 'Club portal'}`;
  const eventCount = Array.isArray(session?.events) ? session.events.length : 0;
  const canSwitch = Boolean(session?.shared) && eventCount > 1;
  const eventId = session?.event?.id;
  const managePath = eventId ? `${secretaryPortalBase(eventId)}/access` : '';

  const actionSlots = useMemo(
    () => ({ desktop: desktopActions, mobile: mobileActions, header: headerActions, compact }),
    [desktopActions, mobileActions, headerActions, compact]
  );

  useEffect(() => {
    setTvVenue('');
    setTvVenues([]);
    setTvVenuesError('');
  }, [eventId]);

  // A portalled action may navigate, so the drawer should not stay over the
  // next page. It also closes if the viewport grows back to desktop width.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!compact) setMobileMenuOpen(false);
  }, [compact]);

  // Secretary portals span every club in the event. A club portal already
  // knows its own venue from the session and needs no lookup.
  useEffect(() => {
    if (!tvDialogOpen || !isSecretary || !token || !eventId) return undefined;

    let cancelled = false;
    setTvVenuesLoading(true);
    setTvVenuesError('');

    getClubs(token)
      .then((response) => {
        if (cancelled) return;

        const seen = new Set();
        const venues = [];

        (response?.clubs || []).forEach((club) => {
          if (club?.venueId === '__unassigned__') return;

          const name = String(club?.venueName || '').trim();
          if (!name) return;

          const key = name.toLowerCase();
          if (seen.has(key)) return;

          seen.add(key);
          venues.push({ id: String(club.venueId ?? name), name });
        });

        venues.sort((a, b) => a.name.localeCompare(b.name));
        setTvVenues(venues);
        setTvVenue((current) =>
          current && !venues.some((venue) => venue.name === current) ? '' : current
        );
      })
      .catch((requestError) => {
        if (!cancelled) setTvVenuesError(portalErrorMessage(requestError));
      })
      .finally(() => {
        if (!cancelled) setTvVenuesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tvDialogOpen, isSecretary, token, eventId, tvVenuesReload]);

  const venueOptions = useMemo(() => {
    if (isSecretary) return tvVenues;

    const venueName = String(session?.scope?.venueName || '').trim();
    if (!venueName) return [];

    return [
      {
        id: String(session?.scope?.venueId || venueName),
        name: venueName
      }
    ];
  }, [isSecretary, session?.scope?.venueId, session?.scope?.venueName, tvVenues]);

  const openTvDialog = () => setTvDialogOpen(true);
  const closeTvDialog = () => setTvDialogOpen(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const openTvFromMenu = () => {
    setMobileMenuOpen(false);
    openTvDialog();
  };
  const logoutFromMenu = () => {
    setMobileMenuOpen(false);
    onLogout();
  };

  const tvVenueHelper = tvVenuesLoading
    ? 'Loading venues…'
    : tvVenuesError
      ? 'The venue list could not be loaded. Whole event is still available.'
      : 'Leave on Whole event to show every venue.';

  return (
    <PortalActionsSlotContext.Provider value={actionSlots}>
      <Box minHeight="100vh" bgcolor="background.default">
        <AppBar position="sticky" elevation={2} sx={{ border: 0 }}>
          <Toolbar sx={{ gap: 1, flexWrap: 'wrap', py: { xs: 1, sm: 0.5 } }}>
            <Box flex={1} minWidth={0}>
              {canSwitch ? (
                <PortalEventSwitcher session={session} kind={session.scope.kind} />
              ) : (
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {eventName}
                </Typography>
              )}
              <Typography variant="caption" display="block" noWrap sx={{ opacity: 0.9 }}>
                {session?.shared ? scopeLabel : `${scopeLabel} · This event only`}
              </Typography>
              {identityName && (
                <Typography variant="caption" display="block" noWrap sx={{ opacity: 0.85 }}>
                  Signed in as {identityName}
                </Typography>
              )}
            </Box>

            <Box
              ref={setDesktopActions}
              sx={{ ...appBarActionsSx, display: { xs: 'none', sm: 'flex' } }}
            />

            {eventId && (
              <Button
                color="inherit"
                startIcon={<LiveTvIcon />}
                endIcon={<ExpandMoreIcon />}
                onClick={openTvDialog}
                aria-haspopup="dialog"
                aria-expanded={tvDialogOpen ? 'true' : undefined}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                TV Logs
              </Button>
            )}

            {isSecretary && managePath && (
              <Button
                component={RouterLink}
                to={managePath}
                color="inherit"
                startIcon={<VpnKeyIcon />}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
                Manage PINs
              </Button>
            )}

            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              Log out
            </Button>

            {/* Page actions that must stay reachable on phones, e.g. Refresh. */}
            <Box
              ref={setHeaderActions}
              sx={{ ...appBarActionsSx, display: { xs: 'flex', sm: 'none' } }}
            />

            <IconButton
              color="inherit"
              aria-label="Open portal menu"
              aria-expanded={mobileMenuOpen ? 'true' : undefined}
              onClick={() => setMobileMenuOpen(true)}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={closeMobileMenu}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: { width: { xs: '85vw', sm: 340 }, maxWidth: 360 }
          }}>
          <Box
            role="presentation"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 2,
                pt: 2,
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
              <Box flex={1} minWidth={0}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {eventName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap>
                  {session?.shared ? scopeLabel : `${scopeLabel} · This event only`}
                </Typography>
                {identityName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    noWrap>
                    Signed in as {identityName}
                  </Typography>
                )}
              </Box>
              <IconButton
                aria-label="Close portal menu"
                onClick={closeMobileMenu}
                edge="end">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box ref={setMobileActions} sx={drawerActionsSx} />

            <List sx={{ py: 0.5 }}>
              {eventId && (
                <ListItemButton
                  sx={drawerItemSx}
                  onClick={openTvFromMenu}
                  aria-haspopup="dialog"
                  aria-expanded={tvDialogOpen ? 'true' : undefined}>
                  <ListItemIcon sx={drawerIconSx}>
                    <LiveTvIcon />
                  </ListItemIcon>
                  <ListItemText primary="TV Logs" />
                </ListItemButton>
              )}
              {isSecretary && managePath && (
                <ListItemButton sx={drawerItemSx} component={RouterLink} to={managePath}>
                  <ListItemIcon sx={drawerIconSx}>
                    <VpnKeyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Manage PINs" />
                </ListItemButton>
              )}
            </List>

            <Divider />

            <List sx={{ py: 0.5 }}>
              <ListItemButton sx={drawerItemSx} onClick={logoutFromMenu}>
                <ListItemIcon sx={drawerIconSx}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary="Log out" />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>

      <Dialog open={tvDialogOpen} onClose={closeTvDialog} maxWidth="xs" fullWidth>
        <DialogTitle>TV Logs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a venue, or show the whole event.
          </Typography>

          <TextField
            select
            fullWidth
            label="Show"
            value={tvVenue}
            onChange={(event) => setTvVenue(event.target.value)}
            SelectProps={{ displayEmpty: true }}
            disabled={tvVenuesLoading}
            error={Boolean(tvVenuesError)}
            helperText={tvVenueHelper}>
            <MenuItem value="">Whole event</MenuItem>
            {venueOptions.map((venue) => (
              <MenuItem key={venue.id} value={venue.name}>
                {venue.name}
              </MenuItem>
            ))}
          </TextField>

          {tvVenuesError && (
            <Button
              size="small"
              onClick={() => setTvVenuesReload((value) => value + 1)}
              sx={{ mt: 0.5 }}>
              Retry venue list
            </Button>
          )}

          <Stack spacing={1.5} sx={{ mt: 2 }}>
            <Button
              component="a"
              href={tvLogsUrl(eventId, tvVenue)}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              startIcon={<LiveTvIcon />}
              onClick={closeTvDialog}
              fullWidth>
              Open TV Logs
            </Button>
            <Button
              component="a"
              href={tvLogsScrollingUrl(eventId, tvVenue)}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              startIcon={<LiveTvIcon />}
              onClick={closeTvDialog}
              fullWidth>
              Open TV Logs Scrolling
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTvDialog}>Close</Button>
        </DialogActions>
      </Dialog>

        <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 }, pb: 6 }}>
          {children}
        </Container>
      </Box>
    </PortalActionsSlotContext.Provider>
  );
};

export default PortalLayout;
